import { util } from '../util';
import _ from 'underscore';
import { HttpDataService, IHttpConfig, IHttpDataCache } from '../http/http-data.service';
import { JQueryPlugins } from '@type_definitions/vendor';
import { GaMessageService } from '../message/ga-message.service';
import { ValueGetterParams } from 'ag-grid-community';
import { Injectable } from '@angular/core';
import { IMailRequest } from '../send-mail/send-mail-data.service';
import { FormattingService } from '../services/formatting.service';
import { IObject } from '@type_definitions/datatypes';
import { AuthService } from '../services/auth/auth.service';
import { GaDeferredService, IGADeferred } from '../services/ga-deferred.service';
import { HttpParams } from '@angular/common/http';
import { ReportDataType, ReportScreenType } from './report-style.service';
import moment from 'moment';
import { ReportColumnSizingType, ReportDisplayType } from '@app/shared/report/report-grid.service';
import { IStoredReportAggregate, IStoredReportColumn } from '@app/shared/report/stored-report-data.service';

export interface IFilterGroup {
  code:string;
  title:string; // title of collapse section
  collapsed:boolean; // default state of collapse per section
  columns:string[]; // define columns within section
  loaded?:boolean; // filter group is loaded
}

export interface IReportMetadata {
  id:number;
  autoLoad:boolean;
  title:string;
  description:string;
  comments:string;
  columns:Array<IColumnMetaData>;
  actions:Array<IReportAction>;
  aggregate:IReportAggregates;
  filterGroups:IFilterGroup[];
  templates:any[];
  permissions:IReportPermissions;
}

export interface IColumnMetaData {
  id:string;
  name:string;
  label:string;
  formattedLabel?:string;
  type:ReportDataType;
  screenType?:ReportScreenType;
  filterScreenType?:string; // this is only used when we have a screenType LINK but we want to display the filter as LIST (dropdown field)
  defaultValue1?:any;
  defaultValue2?:any;
  nullValue1?:any;
  nullValue2?:any;
  hasSubColumns:boolean;
  searchSequence:number;
  listSequence:number;
  formatting?:IColumnFormatting;
  aggregate:IReportAggregates;
  group?:IColumnGroup;
  domain:string;
  groupingSequence:number;
  aggregateOn?:string;
  pivotSequence?:number;
  valueGetter?:(params:ValueGetterParams) => any;
}

export interface IColumnGroup {
  code:string;
  description:string;
  sequence:number;
}

export interface IColumnFormatting {
  xs?:boolean;
  xsm?:boolean;
  sm?:boolean;
  md?:boolean;
  lg?:boolean;
  xl?:boolean;
  minWidth?:number;
  maxWidth?:number;
  align?:string;
  headerdropdown?:boolean;
  showinlist?:boolean;
  showinsearch?:boolean;
  showinexport?:boolean;
  defaultinlist?:boolean;
  defaultinexport?:boolean;
  alwaysIncludeInSearch?:boolean;
  format?:string;
  autoHeight?:boolean;
  stripHtml?:boolean;
}

export interface IReportAction {
  id:number;
  name:string;
  comments?:string;
  code:string;
  mapping?:any;
  ga3FormKey?:string;
  ga3FormID?:string;
  ga3CustomAction?:string;
}

export interface IReportActionResult {
  rows:IReportActionResultRows;
  message:string;
}

export interface IReportActionResultRows {
  [ key:string ]:IReportActionResultRow;
}

export interface IReportActionResultRow {
  message?:string;
  actionStatus?:string;
  row:any;
}

export interface IReportAggregates {
  sum?:boolean;
  count?:boolean;
  average?:boolean;
  min?:boolean;
  max?:boolean;
}

export interface IReportData {
  sql:string;
  data:IReportRow[];
  aggregates:{
    sum:{ [ index:string ]:number };
    count:{ [ index:string ]:number };
    average:{ [ index:string ]:number };
    min:{ [ index:string ]:number };
    max:{ [ index:string ]:number };
  };
}

export interface ISubColumns {
  [ key:string ]:IColumnMetaData[];
}

export interface IReportResponse {
  subColumns?:ISubColumns;
  data:IReportData;
}

export interface IReportRow {
  index:string | number;

  [ index:string ]:string | Date | number | IReportValue;
}

export interface IReportValue {
  value:string | Date | number;
  properties:any;
}

export interface IFilterOption {
  id:string;
  value:string;
}

export interface IReportPermissions {
  listAllowed:boolean;
  insertAllowed:boolean;
  detailAllowed:boolean;
  updateAllowed:boolean;
  deleteAllowed:boolean;
}

export interface IReportFiltersOption {
  [columnName:string]:IFilterOption[];
}

export interface IReportLayoutSetting extends IStoredReportAggregate {
  id?:number;
  displayType?:ReportDisplayType;
  columnSizing?:ReportColumnSizingType;
  ownerId?:number;
  reportId?:number;
  menuId?:number;
  gridCode?:string;
  storedReportId?:string;
  columns?:IReportLayoutSettingColumn[];
  isChanged?:boolean;
}

export interface IReportLayoutSettingColumn extends IStoredReportColumn {
  id?:number;
  columnNumber:number;
}

@Injectable()
export class ReportDataService {
  public static EMPTY_MARKER:string = 'EMPTY';
  private static AGG_MAPPING:IObject = {
    sum: 'Deshsum',
    average: 'Deshavg',
    count: 'Deshcnt',
    min: 'Deshmin',
    max: 'Deshmax'
  };

  private static LOADDATA_REQUEST_ID:string = 'report';
  private static LOADSUBCOLUMNS_REQUEST_ID:string = 'subcolumns';
  private static SENDREPORT_REQUEST_ID:string = 'sendreport';

  private cache:IHttpDataCache<any>;

  constructor(
    private authService:AuthService,
    private formattingService:FormattingService,
    private messageService:GaMessageService,
    private httpDataService:HttpDataService,
    private deferredService:GaDeferredService
  ) {
    this.cache = httpDataService.createCache();
  }

  public clear():void {
    this.cache.clear();
  }

  public loadMetaData(reportId:string | number, hideErrorMessage?:boolean):Promise<IReportMetadata> {
    return this.cache.doGET(
      `rest/reports/${reportId}/metadata`,
      undefined,
      undefined,
      !hideErrorMessage? 'Error loading report metadata' : undefined
    );
  }

  public loadSubColumns = (cancelId:string, reportId:string | number, filters:any, columns:IColumnMetaData[]):Promise<(ISubColumns | IColumnMetaData[])[]> => {
    return this.httpDataService.doPUT(
      `rest/reports/${reportId}/subcolumns`,
      undefined,
      filters,
      undefined,
      'Error loading report metadata',
      ReportDataService.LOADSUBCOLUMNS_REQUEST_ID + cancelId
    )
      .then((subColumns:ISubColumns) => {
        let compiledSubColumns:IColumnMetaData[] = [];

        // for all columns, compile a list of subColumns with the parent column visibility
        _.forEach(columns, (column:IColumnMetaData) => {
          if (column.hasSubColumns) {
            compiledSubColumns.push(column);
            compiledSubColumns = compiledSubColumns.concat(
              _.chain(subColumns[ column.label ])
                .map((subColumn:IColumnMetaData) => {
                  //subColumn takes parent properties such as visibility, group
                  subColumn.formatting.showinlist = column.formatting.showinlist;
                  subColumn.formatting.showinexport = column.formatting.showinexport;
                  subColumn.formatting.defaultinlist = column.formatting.defaultinlist;
                  subColumn.formatting.defaultinexport = column.formatting.defaultinexport;
                  subColumn.group = column.group;

                  return subColumn;
                })
                .value()
            );
          }
        });

        return [
          subColumns,
          compiledSubColumns
        ];
      });
  };

  /**
   * Loads data for given filters
   * @param cancelId The cancellation ID that can be used to cancel the load operation
   * @param reportId The ID of the report
   * @param filters The filters that need to be used for searching
   * @param aggregates The aggregates that need to be computed (only applicable to reports that can provide aggregate data)
   * @param columns The full list of columns in the report: this is only used to limit the retrieval of subcolumn data (because that can take a looooong time)
   * @returns A promise containing the list of subcolumns (can be undefined) and actual report data
   */
  public loadData(cancelId:string, reportId:string | number, filters:any, aggregates?:IReportAggregates, columns?:IColumnMetaData[]):Promise<IReportResponse> {
    const hasSubColumns:boolean = !!_.findWhere(columns, { hasSubColumns: true });

    const loadData = (columnList:IColumnMetaData[]):Promise<IReportData> => {
      const url:string = '/unacc/AjaxResponseHandler';
      let params = {
        ContentType: 'json',
        CallerApp: 'Report',
        Caller: 'Btjson',
        Event: 'onClick',
        Deusrnm: this.authService.getLoginDetails().username,
        Derepid: reportId
      };

      params = _.extend(params, this.formatFilters(filters));

      //aggregates
      _.each((<any>aggregates || []), (aggregateValue:boolean, aggregateKey:string) => {
        if (aggregateValue) {
          params[ ReportDataService.AGG_MAPPING[ aggregateKey ] ] = aggregateValue;
        }
      });

      //columns
      if (columnList) {
        columnList.forEach((column) => {
          if (column.formatting.defaultinlist) {
            params[ column.id + '_Deshowl' ] = 1;
          } else {
            params[ column.id + '_Deshowl' ] = 0;
          }
        });
      }

      const config:IHttpConfig = {
        method: 'POST',
        url: url,
        options: {
          body: $.param(params),
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }
      };

      return this.httpDataService.doHTTP(
        config,
        undefined,
        'Error loading report data',
        ReportDataService.LOADDATA_REQUEST_ID + cancelId,
        true
      );
    };

    if (hasSubColumns) {
      //load + compile subcolumns first
      let subColumns:ISubColumns;
      return this.loadSubColumns(cancelId, reportId, filters, columns)
        .then(util.spread((subColumnDict:ISubColumns, compiledSubColumm:IColumnMetaData[]) => {
          subColumns = subColumnDict;
          return loadData(compiledSubColumm);
        }))
        .then((data:any) => {
          return {
            data,
            subColumns
          };
        });
    } else {
      return loadData(undefined)
        .then((data:IReportData) => {
          return { data };
        });
    }
  }

  public sendReport(mailData: IMailRequest, reportId:number | string, filters:any, aggregates:IReportAggregates, columns?:string[]):Promise<void> {
    const url:string = this.authService.authenticatedUrl('/unacc/AjaxResponseHandler');
    let params:IObject = {
      CallerApp: 'Report',
      Caller: 'Mail',
      Event: 'onPicked',
      fromMailJSP: 1,
      Derepid: reportId,
      Desubje: mailData.subject,
      Demaito: mailData.to, // comma separated list of contact IDs + email addresses
      Demaicc: mailData.cc, // comma separated list of contact IDs + email addresses
      Demaibc: mailData.bcc, // comma separated list of contact IDs + email addresses
      Dembody: mailData.body
    };

    // add report filters
    params = _.extend(params, this.formatFilters(filters));

    // add report aggregates
    _.each((<any>aggregates || []), (aggregateValue:boolean, aggregateKey:string) => {
      if (aggregateValue) {
        params[ ReportDataService.AGG_MAPPING[ aggregateKey ] ] = aggregateValue;
      }
    });

    // add visible columns, when not set the mailed report includes all columns
    if (columns && columns.length > 0) {
      params.columns = columns.join(',');
    }

    const config:IHttpConfig = {
      method: 'POST',
      url: url,
      options: {
        body: $.param(params),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        responseType: <any>'text'
      }
    };

    return this.httpDataService.doHTTP(
      config,
      'Sending…',
      'Error sending report',
      ReportDataService.SENDREPORT_REQUEST_ID + reportId,
      true
    );
  }

  private formatFilters(filters:any):IObject {
    const formattedFilters:IObject = _.mapObject(filters, (filter) => {
      if (moment.isDate(filter)) {
        return this.formattingService.formatDate(filter);
      }
      return filter;
    });
    return formattedFilters;
  }

  public getExcel(reportId:number | string, filters:any, fileName:string, templateId:number, columns?:IColumnMetaData[], columnSequences?:string):Promise<void> {
    const url:string = this.authService.authenticatedUrl('/unacc/ReportExport');

    let data:IObject = {
      Derepid: reportId,
      Detemid: templateId,
      withColumnPage: (columns && columns.length)? 1 : 0,
      columnseq: columnSequences
    };

    if (columns && columns.length) {
      columns.forEach((column) => {
        data[ column.id + '_X' ] = 1;
      });
    }

    data = _.extend(data, this.formatFilters(filters));

    const msg:Noty = this.messageService.showStatusMessage('Exporting report ...');
    const defer:IGADeferred<void> = this.deferredService.defer();

    (<JQueryPlugins>$).fileDownload(url, {
      httpMethod: 'POST',
      data
    })
      .done(() => {
        defer.resolve();
      })
      .fail(() => {
        this.messageService.showErrorMessage('Error exporting report');
        defer.reject();
      })
      .always(() => this.messageService.closeMessage(msg));

    return defer.promise;
  }

  public cancelLoadData(cancelId:string):void {
    this.httpDataService.cancelRequests(ReportDataService.LOADDATA_REQUEST_ID + cancelId);
    this.httpDataService.cancelRequests(ReportDataService.LOADSUBCOLUMNS_REQUEST_ID + cancelId);
  }

  public cancelSendMailData(reportId:string | number):void {
    this.httpDataService.cancelRequests(ReportDataService.SENDREPORT_REQUEST_ID + reportId);
  }

  // get all filters options or all filters options for certain filter group
  public loadAllListFilterOptions(reportId:number, groupCodes?:string[]):Promise<IReportFiltersOption> {
    return this.cache.doGET(
      `rest/reports/${reportId}/columns/list`,
      { groupCode: groupCodes },
      undefined,
      'Error loading options for filters'
    );
  }

  // get list of options for specific column
  public loadListFilterOptions(reportId:string | number, colNumber:string, params?:any):Promise<IFilterOption[]> {
    return this.cache.doGET(
      `rest/reports/${reportId}/columns/${colNumber}/list`,
      params,
      undefined,
      'Error loading filter options'
    );
  }

  // get a single list of filter option
  public loadListFilterOption(reportId:string | number, colNumber:string, optionId:string, params?:any):Promise<IFilterOption> {
    return this.cache.doGET(
      `rest/reports/${reportId}/columns/${colNumber}/list/${optionId}`,
      params,
      undefined,
      'Error loading filter option'
    );
  }

  public executeReportAction(reportId:string | number, actionId:string | number, rows:any, params?:any):Promise<IReportActionResult> {
    return this.httpDataService.doPOST(
      `rest/reports/${reportId}/actions/${actionId}`,
      undefined,
      { rows, params },
      undefined,
      'Error executing action'
    );
  }

  public loadReportActionInfo(reportId:string | number, actionId:string | number, rows:any, hideErrorMessage?:boolean):Promise<any> {
    return this.httpDataService.doPOST(
      `rest/reports/${reportId}/actions/${actionId}/info`,
      undefined,
      {  rows },
      undefined,
      !hideErrorMessage? 'Error loading action info' : undefined,
      undefined,
      true
    );
  }

  public getAuthorizedReports(reportIds:number[], hideErrorMessage?:boolean):Promise<number[]> {
    return this.httpDataService.doGET(
      '/ga2/rest/reports/permissions',
      { reportId: reportIds },
      undefined,
      !hideErrorMessage? 'Error loading report metadata' : undefined
    );
  }

  // generate the URL to GA1 report based on the selected filters
  public generateGa1ReportUrl(reportId?:string, title?:string, tabs?:boolean, autoLoad?:boolean, showFilters?:boolean, filters?:{}):string {
    if (!reportId) {
      return undefined;
    }

    const params:any = {};

    params.row0_Derepid = reportId;
    params.ejbEvent = 'edit';
    params.wv_show_Btactio = 1;
    params.useGA2Reports = false;

    if (title) {
      params.wv_Detitle = title;
    }

    if (tabs) {
      params.Detabme = 1;
    }

    if (autoLoad) {
      params.wv_autoShow = 1;
    }

    if (!showFilters) {
      params.wv_hideFilters = 1;
    }

    if (filters) {
      _.each(filters || {}, (filterValue:any, filterName:string) => {

        // format every date
        if (filterValue instanceof Date) {
          filterValue = this.formattingService.formatDateNoUtc(filterValue.toISOString());
        }

        params[ 'wv_' + filterName ] = filterValue;
      });
    }

    const httpParams:string = new HttpParams({ fromObject: params }).toString();

    return `/unacc/ReportView_Edit.jsp?${httpParams}`;
  }

  public getReportLayoutSettings(gridCodes:string[], menuId?:number, storedReportId?:string):Promise<IReportLayoutSetting[]> {
    return this.httpDataService.doGET(
      'rest/reports/layoutsettings',
      { gridCode: gridCodes, menuId, storedReportId },
      undefined,
      'Error loading report layout settings'
    );
  }

  public saveLayoutSettings(layoutSettings:IReportLayoutSetting[]):Promise<IReportLayoutSetting[]> {
    return this.httpDataService.doPOST(
      'rest/reports/layoutsettings',
      undefined,
      layoutSettings
    );
  }
  
  public deleteLayoutSetting(gridCode:string, menuId?:number, storedReportId?:string):Promise<void> {
    return this.httpDataService.doDELETE(
      'rest/reports/layoutsettings',
      { gridCode, menuId, storedReportId },
      undefined,
      'Error deleting report layout settings'
    );
  }
}